import type {
  Trait,
  LifeChoice,
  WorldDetail,
  Relationship,
  Dossier,
} from "../types";
import type { NarrativeContext, NarrativeResult } from "../narrative";
import {
  fill,
  pick,
  pickDistinct,
  listJoin,
  INTEREST_PHRASE,
  STRENGTH_PHRASE,
  AMBITION_PHRASE,
  LIFE_PATH_PHRASE,
  influenceLead,
} from "./compose";
import { generatePersonName, generateAlienName, generatePlaceName } from "./names";
import { buildUniverseDescription, buildWorldDetails, STRENGTH_TRAIT_MAP } from "./shared";
import { clamp } from "./helpers";
import {
  TONES,
  INVERSIONS,
  UNEXPECTED_INTEREST_ROLES,
  UNEXPECTED_LIFEPATH_STYLES,
  TAGLINE_STARTERS,
  TAGLINE_PIVOTS,
  ORIGIN_SETTINGS,
  ORIGIN_FORMATIVE_EVENTS,
  ORIGIN_EARLY_INSIGHTS,
  TURNING_POINT_PREMISES,
  TURNING_POINT_LIFEPATH_ACTIONS,
  TURNING_POINT_OUTCOMES,
  TODAY_SETTINGS,
  TODAY_ACTIVITIES,
  TODAY_REFLECTIONS,
  CHOICE_TEMPLATES,
} from "./unexpected.banks";

/**
 * Generates an unexpected identity grounded in a logically coherent inverted assumption,
 * derived consequences, reframed answers across all interests and lifepath,
 * and a rich complete dossier.
 */
export function generateUnexpected(ctx: NarrativeContext): NarrativeResult {
  const { name: visitorName, axes, condition, universe, sharedUniverse, resolved } = ctx;

  // Dedicated independent streams for every field
  const nameStream = ctx.stream("name");
  const toneStream = ctx.stream("tone");
  const invStream = ctx.stream("inversion");
  const occStream = ctx.stream("occupation");
  const tagStream = ctx.stream("tagline");
  const titleStream = ctx.stream("title");
  const originStream = ctx.stream("origin");
  const turnStream = ctx.stream("turningPoint");
  const todayStream = ctx.stream("today");
  const choiceStream = ctx.stream("choices");
  const traitStream = ctx.stream("traits");
  const dossierStream = ctx.stream("dossier");

  // 1. Inversion selection and tone flavor
  const tone = pick(TONES, toneStream);
  const inversion = pick(INVERSIONS, invStream);

  // 2. Personal Name (blend of person names and occasional alien names for non-standard universes)
  const isAlienTopology = universe.config.dimensions !== 3 || universe.config.topology !== "flat";
  const useAlienName = (isAlienTopology && nameStream() > 0.4) || nameStream() > 0.75;
  const personalName = useAlienName
    ? generateAlienName(nameStream)
    : generatePersonName(nameStream);

  // 3. Occupation & Title built from ALL resolved interests reframed through inversion x lifePath
  const primaryInterest = resolved.interests[0];
  const secondaryInterests = resolved.interests.slice(1);
  const rolePool = UNEXPECTED_INTEREST_ROLES[primaryInterest] || UNEXPECTED_INTEREST_ROLES.science;
  const primaryRoleBase = pick(rolePool, occStream);
  const stylePool = UNEXPECTED_LIFEPATH_STYLES[resolved.lifePath] || UNEXPECTED_LIFEPATH_STYLES.maker;
  const lifeStyleSuffix = pick(stylePool, occStream);

  // Combine role into a culturally coherent title & occupation
  const occupation = `${primaryRoleBase} & ${lifeStyleSuffix}`;

  // Title / Epithet
  const titleStarters = [
    "Master of Minor Miracles",
    "Inquisitor of Unspoken Truths",
    "Curator of Deliberate Anomalies",
    "Archivist of Unfinished Days",
    "The Pragmatic Dreamer",
    "Warden of the Midnight Workshop",
    "Advocate of Generous Flaws",
    "The Serendipitous Saboteur",
    "Cartographer of Quiet Deviations",
    "Patron of Necessary Blunders",
    "The Irreverent Consoler",
    "Steward of Borrowed Shadows",
  ];
  const title = pick(titleStarters, titleStream);

  // Tagline from combinatorial parts
  const taglineStart = pick(TAGLINE_STARTERS, tagStream);
  const taglinePivot = pick(TAGLINE_PIVOTS, tagStream);
  const tagline = `${taglineStart} ${taglinePivot}`;

  // 4. City / Place name
  const cityName = generatePlaceName(invStream, isAlienTopology ? "alien" : "human");

  // 5. Story Beats (assembled from 3+ recombinable parts with >= 12 variants each)
  const originPart1 = pick(ORIGIN_SETTINGS, originStream);
  const originPart2 = pick(ORIGIN_FORMATIVE_EVENTS, originStream);
  const originPart3 = pick(ORIGIN_EARLY_INSIGHTS, originStream);

  const vars = {
    name: personalName,
    city: cityName,
    daily_rule: inversion.dailyPractice.toLowerCase().replace(/\.$/, ""),
    visitor_name: visitorName,
  };

  const origin = fill(`${originPart1} ${originPart2} ${originPart3}`, vars);

  // Turning Point: Ambition premise x LifePath action x Event outcome
  const turnPremiseList = TURNING_POINT_PREMISES[resolved.ambition] || TURNING_POINT_PREMISES.understand;
  const turnPart1 = pick(turnPremiseList, turnStream);
  const turnActionList = TURNING_POINT_LIFEPATH_ACTIONS[resolved.lifePath] || TURNING_POINT_LIFEPATH_ACTIONS.maker;
  const turnPart2 = pick(turnActionList, turnStream);
  const turnPart3 = pick(TURNING_POINT_OUTCOMES, turnStream);

  const turningPoint = fill(`${turnPart1} ${turnPart2} ${turnPart3}`, vars);

  // Today Beat: Setting x Activity x Reflection
  const todayPart1 = pick(TODAY_SETTINGS, todayStream);
  const todayPart2 = pick(TODAY_ACTIVITIES, todayStream);
  const todayPart3 = pick(TODAY_REFLECTIONS, todayStream);

  const today = fill(`${todayPart1} ${todayPart2} ${todayPart3}`, vars);

  // 6. Choices (exactly 3 distinct kinds with different age strings)
  const pickedChoiceTemplates = pickDistinct(CHOICE_TEMPLATES, 3, choiceStream);
  const choices: LifeChoice[] = pickedChoiceTemplates.map((template) => ({
    age: template.age,
    choice: fill(template.choice, vars),
    roadNotTaken: fill(template.roadNotTaken, vars),
    consequence: fill(template.consequence, vars),
  }));

  // 7. Traits: exactly 4 from all ctx.resolved.strengths via STRENGTH_TRAIT_MAP but reframed
  const traits: Trait[] = [];
  const strengthPool = [...resolved.strengths];
  // ensure we have at least 4 items to draw from
  while (strengthPool.length < 4) {
    strengthPool.push("humor", "creativity", "courage", "empathy");
  }

  for (let i = 0; i < 4; i++) {
    const sId = strengthPool[i % strengthPool.length];
    const items = STRENGTH_TRAIT_MAP[sId] || STRENGTH_TRAIT_MAP.humor;
    const traitItem = pick(items, traitStream);

    // Map base level to appropriate axis with jitter
    const baseLevel =
      sId === "humor"
        ? axes.curiosity
        : sId === "creativity"
        ? axes.courage
        : sId === "courage"
        ? axes.courage
        : sId === "empathy"
        ? axes.connection
        : sId === "discipline"
        ? axes.stability
        : sId === "logic"
        ? axes.curiosity
        : sId === "patience"
        ? axes.stability
        : axes.influence;

    const jitter = Math.floor(traitStream() * 14) - 7;
    traits.push({
      name: traitItem.name,
      description: traitItem.description,
      level: clamp(baseLevel + jitter, 25, 95),
    });
  }

  // 8. World details: buildWorldDetails from shared.ts + 3 extra details tied to the inversion
  const extraDetails: { label: string; value: string }[] = [
    { label: "Civic Axiom", value: inversion.socialOrder },
    { label: "Daily Protocol", value: inversion.dailyPractice },
    { label: "Cultural Vibe", value: inversion.civicVibe },
  ];

  // Weave ALL resolved interests into local interest detail
  const interestPhrases = resolved.interests.map((id) => INTEREST_PHRASE[id].humanDomain);
  const interestDetail = `Inverting orthodox assumptions about ${listJoin(interestPhrases)} under the framework of ${inversion.ruleTitle}.`;

  const worldDetails = buildWorldDetails("unexpected", universe, condition, interestDetail, extraDetails);
  const universeDescription = buildUniverseDescription(universe, condition, sharedUniverse);

  // 9. Dossier Generation (rich, multi-part, specific)
  const dRng = dossierStream;

  // Background (2-4 sentences: family, place/era, education/formation)
  const bgSentence1 = `Formed in ${cityName} during an era governed by ${inversion.ruleTitle}, ${personalName} was raised in a household where ${inversion.dailyPractice.toLowerCase().replace(/\.$/, "")}.`;
  const bgSentence2 = `Early schooling rejected standardized academic laurels in favor of ${LIFE_PATH_PHRASE[resolved.lifePath].manner}, instilling a lifelong devotion to ${INTEREST_PHRASE[primaryInterest].noun}.`;
  const bgSentence3 = secondaryInterests.length > 0
    ? `Apprenticeships across ${listJoin(secondaryInterests.map((id) => INTEREST_PHRASE[id].field))} exposed the underlying absurdities of orthodox trade guilds, cementing an unconventional creative posture.`
    : `Surrounded by rigid civic protocols, ${personalName} learned that true mastery lies in navigating rules with gentle, subversive wit rather than solemn compliance.`;
  const background = `${bgSentence1} ${bgSentence2} ${bgSentence3}`;

  // Personality (2-3 sentences, specific)
  const toneDesc =
    tone === "wry"
      ? "Radiates dry, ironic warmth and an irrepressible knack for puncturing pomposity without making enemies."
      : tone === "eerie"
      ? "Quietly observant with an uncanny ability to notice anomalous patterns that others reflexively ignore."
      : tone === "tender"
      ? "Deeply compassionate and endlessly patient, approaching every fragile companion with disarming gentleness."
      : tone === "absurd"
      ? "Delightfully eccentric and kinetic, treating grave civilizational impasses as invitations to exuberant invention."
      : "Carries a bittersweet, contemplative grace that finds profound poetry within fleeting, imperfect moments.";

  const personalitySentence2 = `Possesses ${STRENGTH_PHRASE[resolved.strengths[0]].asset}, refusing to bow to dogmatic certainty.`;
  const personalitySentence3 = `Moves through council halls and market lanes alike with an unhurried, reassuring presence that immediately puts nervous apprentices at ease.`;
  const personality = `${toneDesc} ${personalitySentence2} ${personalitySentence3}`;

  // Skills (4-6 short phrases)
  const skills: string[] = [
    `Bespoke ${primaryRoleBase.toLowerCase()} methods`,
    `Reframing ${INTEREST_PHRASE[primaryInterest].verb} into civic celebrations`,
    `${STRENGTH_PHRASE[resolved.strengths[0]].habit}`,
    `Navigating ${inversion.ruleTitle.toLowerCase()} protocols`,
  ];
  if (resolved.strengths.length > 1) {
    skills.push(`Tactical application of ${STRENGTH_PHRASE[resolved.strengths[1]].noun}`);
  }
  if (secondaryInterests.length > 0) {
    skills.push(`Lateral integration of ${INTEREST_PHRASE[secondaryInterests[0]].noun}`);
  } else {
    skills.push("Subversive institutional mediation");
  }

  // Motivations (2-3 sentences/phrases from ambition)
  const motivations: string[] = [
    `Driven by ${AMBITION_PHRASE[resolved.ambition].drive}, reframing solemn obligations into joyful shared discoveries.`,
    `Demonstrating that embracing ${inversion.premise.toLowerCase().replace(/\.$/, "")} can liberate human creativity rather than crush it.`,
  ];
  if (dRng() > 0.4) {
    motivations.push(`Shielding vulnerable neighbors from the punitive rigidity of standardized civic codes.`);
  }

  // Relationships (exactly 3 distinct roles)
  const mentorName = generatePersonName(dRng);
  const rivalName = generatePersonName(dRng);
  const companionName = generatePersonName(dRng);

  const relationships: Relationship[] = [
    {
      who: mentorName,
      role: "Eccentric Mentor & Clockwork Horologist",
      bond: `${personalName} inherited both the workshop tools and the subversive philosophy that guides their daily craft from ${mentorName}. Their enduring letters remain a touchstone of sanity amid bureaucratic chaos.`,
    },
    {
      who: rivalName,
      role: "Formal Civic Registrar & Ideological Foil",
      bond: `A strictly orthodox official who views ${personalName}'s methods as dangerous nonsense, yet secretly consults ${personalName} whenever municipal emergencies defy the rulebook.`,
    },
    {
      who: companionName,
      role: "Founding Collaborator & Master Baker",
      bond: `A steadfast confidant who stood beside ${personalName} during the darkest aftermath of their career turning point. Together, they share evening tea, unvarnished criticisms, and unshakeable loyalty.`,
    },
  ];

  // Achievements (2-3 sentences)
  const achievements: string[] = [
    `Engineered the beloved communal contraption in ${cityName}'s market square that defused a three-year civil standoff between rival trade syndicates.`,
    `Authored an underground manual on ${inversion.ruleTitle.toLowerCase()} that helped hundreds of impoverished households retain their legal standing.`,
  ];
  if (dRng() > 0.5) {
    achievements.push(`Established a free apprenticeship guild that has trained forty-two unclassifiable artisans in bespoke craft.`);
  }

  // Failures (exactly 2 sentences)
  const fail1 = `Failed to prevent the demolition of the old harbor conservatory when legal filings were delayed by an ill-timed municipal prank.`;
  const fail2 = `Alienated a close childhood circle for several seasons by refusing to compromise on an unpopular artistic principle during the winter festival.`;
  const failures: string[] = [fail1, fail2];

  // Defining Dilemma (2-3 sentences following from the inversion)
  const dilemma = pick(inversion.dilemmas, dRng);

  // Trajectory: nearFuture (1-3 sentences) + longArc (1-3 sentences)
  const nearFuture = `Over the coming seasons, ${personalName} intends to finalize the blueprints for an open-source neighborhood guildhall while defending the workshop against a pending zoning audit.`;
  const longArc = `Across decades, this life's enduring legacy will be remembered not in dry municipal histories, but in the irrepressible culture of laughter, compassion, and creative freedom left behind in the streets of ${cityName}.`;
  const trajectory = { nearFuture, longArc };

  // Influence: baseline lead + 2-3 specific sentences on how answers shaped this self
  const leadSentences = influenceLead(ctx);
  const answerSentences: string[] = [];

  // Influence of interests
  if (resolved.interests.length > 1) {
    const list = listJoin(resolved.interests.map((id) => INTEREST_PHRASE[id].noun));
    answerSentences.push(
      `Your combined curiosity toward ${list} directly inspired this persona's hybrid trade as ${occupation.toLowerCase()}.`
    );
  } else {
    answerSentences.push(
      `Your focus on ${INTEREST_PHRASE[primaryInterest].noun} was radically reframed through the inverted societal rules of "${inversion.ruleTitle}."`
    );
  }

  // Influence of life path
  answerSentences.push(
    `Your chosen disposition as "${LIFE_PATH_PHRASE[resolved.lifePath].role}" shaped how ${personalName} responds to institutional friction—choosing ${LIFE_PATH_PHRASE[resolved.lifePath].manner} over bitter confrontation.`
  );

  // Inversion thematic tie
  answerSentences.push(
    `The premise of an inverted social reality (${inversion.premise.toLowerCase().replace(/\.$/, "")}) tested your core virtue of ${STRENGTH_PHRASE[resolved.strengths[0]].noun}, turning an apparent limitation into an art of living.`
  );

  const influence = [...leadSentences, ...answerSentences];

  const dossier: Dossier = {
    background,
    personality,
    skills,
    motivations,
    relationships,
    achievements,
    failures,
    dilemma,
    trajectory,
    influence,
  };

  return {
    name: personalName,
    title,
    tagline,
    occupation,
    traits,
    story: { origin, turningPoint, today },
    choices,
    worldDetails,
    universeDescription,
    dossier,
  };
}
